import React from 'react';
import type { FaqItem } from './FAQ';
import IconGallery from './IconGallery';

type Props = {
  items: FaqItem[];
  palette?: 'indigo' | 'violet' | 'emerald' | 'rose' | 'amber' | 'sky' | 'slate';
  style?: 'classic' | 'card' | 'underline';
  showIcons?: boolean;
  icon?: string;
  onChange: (next: { items: FaqItem[]; palette?: Props['palette']; style?: Props['style']; showIcons?: boolean; icon?: string }) => void;
};

const label = 'text-xs font-medium text-gray-600';
const input = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500';

const FAQSettings: React.FC<Props> = ({ items, palette = 'indigo', style = 'classic', showIcons = false, icon = 'help', onChange }) => {
  const [list, setList] = React.useState<FaqItem[]>(items || []);
  const [pal, setPal] = React.useState<Props['palette']>(palette);
  const [sty, setSty] = React.useState<Props['style']>(style);
  const [iconsOn, setIconsOn] = React.useState<boolean>(!!showIcons);
  const [ico, setIco] = React.useState<string>(icon || 'help');
  const [galleryOpen, setGalleryOpen] = React.useState(false);

  const emit = (next: FaqItem[], p = pal, s = sty, si = iconsOn, ic = ico) => {
    setList(next);
    onChange({ items: next, palette: p, style: s, showIcons: si, icon: ic });
  };

  const updateItem = (i: number, patch: Partial<FaqItem>) => {
    const next = list.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    emit(next);
  };

  const addItem = () => emit([...(list || []), { q: 'New question', a: '' }]);
  const removeItem = (i: number) => emit((list || []).filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-sm font-semibold text-gray-900">Appearance</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={label}>Color palette</label>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={pal}
              onChange={(e)=> { const v = e.target.value as Props['palette']; setPal(v); onChange({ items: list, palette: v, style: sty }); }}
            >
              <option value="indigo">Indigo</option>
              <option value="violet">Violet</option>
              <option value="emerald">Emerald</option>
              <option value="rose">Rose</option>
              <option value="amber">Amber</option>
              <option value="sky">Sky</option>
              <option value="slate">Slate</option>
            </select>
          </div>
          <div>
            <label className={label}>Styling</label>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={sty}
              onChange={(e)=> { const v = e.target.value as Props['style']; setSty(v); onChange({ items: list, palette: pal, style: v }); }}
            >
              <option value="classic">Classic (dividers)</option>
              <option value="card">Card</option>
              <option value="underline">Underline</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between rounded-lg border border-gray-200 p-3">
          <div>
            <div className="text-sm font-medium text-gray-900">Show icons</div>
            <div className="text-xs text-gray-500">Display a small icon before each question.</div>
          </div>
          <button
            type="button"
            onClick={() => { const next = !iconsOn; setIconsOn(next); onChange({ items: list, palette: pal, style: sty, showIcons: next, icon: ico }); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${iconsOn ? 'bg-purple-600' : 'bg-gray-300'}`}
            aria-pressed={iconsOn}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${iconsOn ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </div>
        <div className="mt-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={!iconsOn}
            onClick={()=> setGalleryOpen(true)}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            Choose icon
          </button>
          <IconGallery
            open={galleryOpen}
            onClose={()=> setGalleryOpen(false)}
            onSelect={(name)=> { setIco(name); onChange({ items: list, palette: pal, style: sty, showIcons: iconsOn, icon: name }); }}
          />
        </div>
      </div>
      <div>
        <label className={label}>Items</label>
        <div className="mt-2 space-y-3">
          {(list || []).map((it, i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-3">
              <input className={input + ' mb-2'} value={it.q || ''} onChange={(e)=> updateItem(i, { q: e.target.value })} placeholder="Question" />
              <textarea className={input} rows={3} value={it.a || ''} onChange={(e)=> updateItem(i, { a: e.target.value })} placeholder="Answer" />
              <div className="mt-2 flex items-center justify-end">
                <button className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={()=> removeItem(i)}>
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m-1 0l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div>
            <button className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-50" onClick={addItem}>
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              Add item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSettings;
